/**
 * Content Assembly Service
 * API calls for the FK Partners Content Assembly System
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const API_URL = `${API_BASE_URL}/content-assembly`;

class ContentAssemblyService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  // ═══════════════════════════════════════════════════════════════
  // Trilha Methods
  // ═══════════════════════════════════════════════════════════════

  /**
   * Upload a trilha .docx file
   */
  async uploadTrilha(file: File, createdBy?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (createdBy) {
      formData.append('createdBy', createdBy);
    }

    const response = await axios.post(`${API_URL}/trilhas`, formData, {
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Get all trilhas with pagination
   */
  async getTrilhas(page: number = 1, limit: number = 10): Promise<any> {
    const response = await axios.get(`${API_URL}/trilhas`, {
      headers: this.getAuthHeaders(),
      params: { page, limit },
    });

    return response.data;
  }

  /**
   * Get a single trilha by ID
   */
  async getTrilha(id: string): Promise<any> {
    const response = await axios.get(`${API_URL}/trilhas/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Delete a trilha
   */
  async deleteTrilha(id: string): Promise<any> {
    const response = await axios.delete(`${API_URL}/trilhas/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════
  // Apostila Methods
  // ═══════════════════════════════════════════════════════════════

  /**
   * Create a new apostila assembly job
   */
  async createApostila(params: {
    trilhaId: string;
    name?: string;
    pageLimit?: number;
    addPageNumbers?: boolean;
    includeResumo?: boolean;
    summaryType?: 'oa' | 'full' | 'none'; // NBA-RQ-3: mutually exclusive summary options
    sharePointDriveId?: string;
    sharePointFolderId?: string;
    createdBy?: string;
  }): Promise<any> {
    const response = await axios.post(`${API_URL}/apostilas`, params, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Get all apostilas with pagination
   */
  async getApostilas(
    filters?: {
      trilhaId?: string;
      course?: string;
      status?: string;
    },
    page: number = 1,
    limit: number = 10
  ): Promise<any> {
    const response = await axios.get(`${API_URL}/apostilas`, {
      headers: this.getAuthHeaders(),
      params: { ...filters, page, limit },
    });

    return response.data;
  }

  /**
   * Get a single apostila by ID
   */
  async getApostila(id: string): Promise<any> {
    const response = await axios.get(`${API_URL}/apostilas/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Delete an apostila
   */
  async deleteApostila(id: string): Promise<any> {
    const response = await axios.delete(`${API_URL}/apostilas/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Download apostila PDF, or a DOCX previously converted via startDocxConversion()
   */
  async downloadApostila(id: string, partNumber?: number, format: 'pdf' | 'docx' = 'pdf'): Promise<Blob> {
    const params: Record<string, string | number> = { format };
    if (partNumber) params.part = partNumber;

    const response = await axios.get(`${API_URL}/apostilas/${id}/download`, {
      headers: this.getAuthHeaders(),
      params,
      responseType: 'blob',
    });

    return response.data;
  }

  /**
   * Start (or check) a background PDF->DOCX conversion job for an apostila part.
   * Returns immediately; poll getDocxStatus() for progress.
   */
  async startDocxConversion(id: string, partNumber?: number): Promise<{ status: 'pending' | 'processing' | 'completed' | 'failed' }> {
    const params: Record<string, number> = {};
    if (partNumber) params.part = partNumber;

    const response = await axios.post(`${API_URL}/apostilas/${id}/convert-docx`, null, {
      headers: this.getAuthHeaders(),
      params,
    });

    return response.data;
  }

  /**
   * Poll the status of a DOCX conversion job.
   */
  async getDocxStatus(id: string, partNumber?: number): Promise<{ status: 'pending' | 'processing' | 'completed' | 'failed'; progress?: number; error?: string }> {
    const params: Record<string, number> = {};
    if (partNumber) params.part = partNumber;

    const response = await axios.get(`${API_URL}/apostilas/${id}/docx-status`, {
      headers: this.getAuthHeaders(),
      params,
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════
  // NBA-RQ-3: Add Slides merge (content → slides → summary)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Start the background Add Slides merge for a completed apostila.
   * Returns immediately; poll getSlidesStatus() for progress.
   */
  async addSlides(id: string, payload: {
    // oeCode optional: extra decks not tied to an OA are allowed (client 2026-10);
    // array order = final order in the document
    slides: { oeCode?: string; fileId: string; driveId: string; fileName?: string }[];
    slidesPerPage: 1 | 2 | 4;
  }): Promise<{ status: string }> {
    const response = await axios.post(`${API_URL}/apostilas/${id}/add-slides`, payload, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Poll the Add Slides merge status (includes the completion report).
   */
  async getSlidesStatus(id: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    currentStep?: string;
    error?: string;
    report?: { addedOEs: string[]; skippedOEs: { oeCode: string; fileName?: string; reason: string }[] };
  }> {
    const response = await axios.get(`${API_URL}/apostilas/${id}/slides-status`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Download the slides-merged apostila PDF (the original stays available).
   */
  async downloadMergedApostila(id: string, partNumber?: number): Promise<Blob> {
    const params: Record<string, number> = {};
    if (partNumber) params.part = partNumber;
    params._t = Date.now(); // cache-buster: a re-merged file lives at the same URL

    const response = await axios.get(`${API_URL}/apostilas/${id}/download-merged`, {
      headers: this.getAuthHeaders(),
      params,
      responseType: 'blob',
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════
  // Resumo (Summaries) Methods
  // ═══════════════════════════════════════════════════════════════

  /**
   * Create a new resumo assembly job
   */
  async createResumo(params: {
    trilhaId: string;
    name?: string;
    pageLimit?: number;
    addPageNumbers?: boolean;
    sharePointDriveId?: string;
    sharePointFolderId?: string;
    createdBy?: string;
  }): Promise<any> {
    const response = await axios.post(`${API_URL}/resumos`, params, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Get all resumos with pagination
   */
  async getResumos(page: number = 1, limit: number = 10): Promise<any> {
    const response = await axios.get(`${API_URL}/resumos`, {
      headers: this.getAuthHeaders(),
      params: { page, limit },
    });

    return response.data;
  }

  /**
   * Get a single resumo by ID
   */
  async getResumo(id: string): Promise<any> {
    const response = await axios.get(`${API_URL}/resumos/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Delete a resumo
   */
  async deleteResumo(id: string): Promise<any> {
    const response = await axios.delete(`${API_URL}/resumos/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Download resumo PDF part
   */
  async downloadResumoPart(id: string, partIndex: number = 0): Promise<Blob> {
    const response = await axios.get(`${API_URL}/resumos/${id}/download/${partIndex}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════
  // Utility Methods
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get assembly statistics
   */
  async getStats(): Promise<any> {
    const response = await axios.get(`${API_URL}/stats`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Get OE cache statistics
   */
  async getCacheStats(): Promise<any> {
    const response = await axios.get(`${API_URL}/cache/stats`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Clear OE cache
   */
  async clearCache(): Promise<any> {
    const response = await axios.delete(`${API_URL}/cache`, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  // ═══════════════════════════════════════════════════════════════
  // AI Book Agent Methods
  // ═══════════════════════════════════════════════════════════════

  /**
   * Generate book outline/structure using AI
   */
  async generateAIBookOutline(params: {
    trilhaId: string;
    bookTitle?: string;
  }): Promise<any> {
    const response = await axios.post(`${API_URL}/ai-book/outline`, params, {
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }
}

export const contentAssemblyService = new ContentAssemblyService();
